import { computed, type ReadonlySignal, type Signal, signal } from "@preact/signals";
import type {
  AgentState,
  BrowserInfo,
  ChatLine,
  FileEntry,
  OrgInfo,
  Snapshot,
  WorkspaceCommand,
  WorkspaceEvent,
  WorkspaceResult,
} from "~/protocol";
import { fileUrl, getCredits, wsUrl } from "~/web/src/api";
import { billingOn } from "~/web/src/billing-on";

// The project channel (spec: docs/project/web-workspace-spec.md,
// workspace-module.md's v2 note): one seam between the UI and a workspace —
// connect(id) → signals mirroring the snapshot, send(command) → its result,
// file URL builder. v2 swaps the transport behind this without touching the UI.
// Reconnects with backoff; the snapshot on (re)connect resets every signal.

/** A Command before the channel assigns its id (Omit distributed over the union). */
export type Outgoing = WorkspaceCommand extends infer C
  ? C extends WorkspaceCommand
    ? Omit<C, "id">
    : never
  : never;

export interface Channel {
  readonly id: string;
  connected: ReadonlySignal<boolean>;
  project: Signal<Snapshot["project"] | null>;
  agent: Signal<AgentState>;
  chat: Signal<ChatLine[]>;
  files: Signal<FileEntry[]>;
  browser: Signal<BrowserInfo | null>;
  /** The viewer's org at connect — the upload limit follows `subscribed`; null before the snapshot. */
  org: Signal<OrgInfo | null>;
  /** The one-shot `progress` estimate; null once anything else arrives (or the socket drops). */
  progress: Signal<Progress | null>;
  /** The org's balance beside the Unlock buttons; null until read, and always with billing off.
   * Read at every (re)connect and by refreshCredits, then kept by the unlock's `credits` events
   * and its result. */
  credits: Signal<number | null>;
  /** Reads the balance again (the Files sheet opening): another member may have spent. */
  refreshCredits(): void;
  /** Fires for every file/file_removed event — tabs watch the names they show. */
  onFile(fn: (name: string, entry: FileEntry | null) => void): () => void;
  /** Fires for every open_tab push (the entry's `file` event always precedes it). */
  onOpenTab(fn: (name: string) => void): () => void;
  /** Fires when a `browser` event carries a live view — the explorer opened a new browser.
   * The snapshot's browser never fires it, so reconnects and reloads stay silent. */
  onBrowserOpen(fn: () => void): () => void;
  send(cmd: Outgoing): Promise<WorkspaceResult>;
  fileUrl(name: string, version?: number): string;
  close(): void;
}

/** The bar (`seconds`, from a `progress` event) or the wait for a render slot (`ahead`, from
 * a `queued` one). */
export type Progress = {
  task: string;
  /** Distinct per event so the UI restarts its animation on a repeat. */
  key: number;
} & ({ seconds: number } | { ahead: number });

const RECONNECT_MS = [500, 1000, 2000, 5000];

export function connect(id: string): Channel {
  const connected = signal(false);
  const project = signal<Snapshot["project"] | null>(null);
  const agent = signal<AgentState>({ status: "idle", active: "director" });
  const chat = signal<ChatLine[]>([]);
  const files = signal<FileEntry[]>([]);
  const browser = signal<BrowserInfo | null>(null);
  const org = signal<OrgInfo | null>(null);
  const progress = signal<Progress | null>(null);
  let progressKey = 0;
  const credits = signal<number | null>(null);
  // Bumped by every balance the workspace pushes: a fetch sent before one and answered after
  // it is older news, and must not put a held credit back on the screen.
  let creditsPushed = 0;
  const setCredits = (n: number) => {
    creditsPushed++;
    credits.value = n;
  };
  function refreshCredits(): void {
    if (!billingOn) return;
    const sent = creditsPushed;
    getCredits().then(
      (c) => {
        if (creditsPushed === sent) credits.value = c.credits;
      },
      () => {}, // the number stays as it was; the next open or unlock reads it again
    );
  }
  const fileWatchers = new Set<(name: string, entry: FileEntry | null) => void>();
  const tabWatchers = new Set<(name: string) => void>();
  const browserWatchers = new Set<() => void>();
  const pending = new Map<number, (r: WorkspaceResult) => void>();
  let nextId = 1;
  let ws: WebSocket | null = null;
  let closed = false;
  let attempt = 0;

  const sortFiles = (list: FileEntry[]) => [...list].sort((a, b) => a.name.localeCompare(b.name));

  function apply(event: WorkspaceEvent): void {
    // Any traffic but a fresh estimate or queue position ends the bar — the render's mp4
    // lands as a `file` event, a failed render shows up as the director's next line.
    progress.value =
      event.kind === "progress"
        ? { task: event.task, seconds: event.seconds, key: ++progressKey }
        : event.kind === "queued"
          ? { task: event.task, ahead: event.ahead, key: ++progressKey }
          : null;
    switch (event.kind) {
      case "snapshot": {
        const s = event.snapshot;
        project.value = s.project;
        agent.value = s.agent;
        chat.value = s.chat;
        files.value = sortFiles(s.files);
        browser.value = s.browser;
        org.value = s.org;
        refreshCredits();
        break;
      }
      case "chat":
        chat.value = [...chat.value, event.line];
        break;
      case "agent":
        agent.value = event.agent;
        break;
      case "file":
        files.value = sortFiles([
          ...files.value.filter((f) => f.name !== event.entry.name),
          event.entry,
        ]);
        for (const fn of fileWatchers) fn(event.entry.name, event.entry);
        break;
      case "file_removed":
        files.value = files.value.filter((f) => f.name !== event.name);
        for (const fn of fileWatchers) fn(event.name, null);
        break;
      case "browser":
        browser.value = event.browser;
        if (event.browser) for (const fn of browserWatchers) fn();
        break;
      case "project":
        project.value = event.project;
        break;
      case "open_tab":
        for (const fn of tabWatchers) fn(event.name);
        break;
      case "progress":
      case "queued":
        break;
      case "credits":
        setCredits(event.credits);
        break;
      case "result":
        if (event.payload?.kind === "unlock_video") setCredits(event.payload.credits);
        pending.get(event.id)?.(event);
        pending.delete(event.id);
        break;
    }
  }

  function open(): void {
    if (closed) return;
    const socket = new WebSocket(wsUrl(id));
    ws = socket;
    socket.onopen = () => {
      attempt = 0;
      connected.value = true;
    };
    socket.onmessage = (m) => {
      if (typeof m.data === "string") apply(JSON.parse(m.data) as WorkspaceEvent);
    };
    socket.onclose = () => {
      connected.value = false;
      progress.value = null;
      ws = null;
      for (const [cid, resolve] of pending) {
        resolve({ kind: "result", id: cid, ok: false, error: "disconnected" });
      }
      pending.clear();
      if (closed) return;
      const delay = RECONNECT_MS[Math.min(attempt++, RECONNECT_MS.length - 1)] ?? 5000;
      setTimeout(open, delay);
    };
    socket.onerror = () => socket.close();
  }
  open();

  return {
    id,
    connected: computed(() => connected.value),
    project,
    agent,
    chat,
    files,
    browser,
    org,
    progress,
    credits,
    refreshCredits,
    onFile(fn) {
      fileWatchers.add(fn);
      return () => fileWatchers.delete(fn);
    },
    onOpenTab(fn) {
      tabWatchers.add(fn);
      return () => tabWatchers.delete(fn);
    },
    onBrowserOpen(fn) {
      browserWatchers.add(fn);
      return () => browserWatchers.delete(fn);
    },
    send(cmd) {
      const cid = nextId++;
      return new Promise<WorkspaceResult>((resolve) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          resolve({ kind: "result", id: cid, ok: false, error: "disconnected" });
          return;
        }
        pending.set(cid, resolve);
        ws.send(JSON.stringify({ ...cmd, id: cid }));
      });
    },
    fileUrl: (name, version) => fileUrl(id, name, version),
    close() {
      closed = true;
      ws?.close();
    },
  };
}
